export type CabinClass = 'economy' | 'premium_economy' | 'business' | 'first';
export type SeatPreference = 'window' | 'aisle' | 'no_preference';
export type TripPace = 'relaxed' | 'balanced' | 'packed';

export type TravelPreferences = {
    homeAirport: string;
    currency: string;
    cabin: CabinClass;
    seat: SeatPreference;
    extraLegroom: boolean;
    preferredAirlines: string[];
    avoidedAirlines: string[];
    maxStops: 0 | 1 | 2;
    hotelAmenities: string[];
    propertyTypes: string[];
    minHotelRating: number;
    budgetPerNightMin: number;
    budgetPerNightMax: number;
    favoriteDestinations: string[];
    interests: string[];
    pace: TripPace;
    travellingWithKids: boolean;
    accessibilityNeeds: string;
    dietaryNeeds: string[];
};

export type NotificationSettings = {
    priceDropAlerts: boolean;
    bookingReminders: boolean;
    weeklyInspiration: boolean;
    productUpdates: boolean;
};

export type BookingStatus = 'confirmed' | 'pending' | 'completed' | 'cancelled';
export type BookingKind = 'flight' | 'hotel' | 'car' | 'activity';

export type Booking = {
    id: string;
    /** The profile that owns this booking — see ProfileState.userId. */
    userId: string;
    kind: BookingKind;
    title: string;
    subtitle?: string;
    startDate: string;
    endDate: string;
    amount: number;
    currency: string;
    status: BookingStatus;
    reference?: string;
    destination?: string;
    /** Thumbnail shown on the Trips list. Optional — a booking without one
     * (e.g. mapped from a real order_confirmation with no photo data)
     * falls back to a kind icon. */
    image?: string;
};

export type SavedTrip = {
    id: string;
    /** The profile that owns this saved trip — see ProfileState.userId. */
    userId: string;
    title: string;
    destination: string;
    note?: string;
    savedAt: string;
};

export type ActivityEntry = {
    id: string;
    text: string;
    at: string;
    kind: 'booking' | 'search' | 'save' | 'account';
};

export type ProfileState = {
    /** Once logged in, Auth0's stable `sub` claim — the real, login-tied
     * identity a backend should key on. Before that (or if never logged
     * in), a per-device id generated on first load so the app still works
     * for a guest — see ProfileContext's hydrate() and its Auth0-sync
     * effect. Every Booking and SavedTrip this profile creates carries
     * whichever of the two was current as its own userId. Empty only in
     * the static EMPTY_PROFILE default; ProfileContext always fills it in
     * before this shape reaches a component. */
    userId: string;
    displayName: string;
    bio: string;
    homeCity: string;
    preferences: TravelPreferences;
    notifications: NotificationSettings;
    bookings: Booking[];
    savedTrips: SavedTrip[];
    activity: ActivityEntry[];
};

export const AIRLINE_OPTIONS = [
    'Delta', 'United', 'American', 'British Airways', 'Air France', 'KLM',
    'Lufthansa', 'Emirates', 'Qatar Airways', 'TAP Air Portugal', 'Iberia', 'Vueling',
];

export const AMENITY_OPTIONS = [
    'Free WiFi', 'Breakfast included', 'Pool', 'Gym', 'Spa', 'Parking',
    'Air conditioning', 'Kitchen', 'Pet friendly', 'Airport shuttle', 'Sea view', 'Workspace',
];

export const PROPERTY_TYPE_OPTIONS = [
    'Hotel', 'Boutique hotel', 'Apartment', 'Villa', 'Guesthouse', 'Hostel', 'Resort',
];

export const INTEREST_OPTIONS = [
    'Food & drink', 'Culture & history', 'Beaches', 'Hiking & nature', 'Nightlife',
    'Shopping', 'Art & museums', 'Architecture', 'Wellness', 'Adventure sports',
    'Photography', 'Wine & vineyards',
];

export const DIETARY_OPTIONS = [
    'Vegetarian', 'Vegan', 'Halal', 'Kosher', 'Gluten-free', 'Nut allergy', 'Dairy-free',
];

export const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CHF', 'JPY', 'AUD', 'CAD'];

export const DEFAULT_PREFERENCES: TravelPreferences = {
    homeAirport: '',
    currency: 'USD',
    cabin: 'economy',
    seat: 'no_preference',
    extraLegroom: false,
    preferredAirlines: [],
    avoidedAirlines: [],
    maxStops: 1,
    hotelAmenities: ['Free WiFi'],
    propertyTypes: [],
    minHotelRating: 0,
    budgetPerNightMin: 80,
    budgetPerNightMax: 350,
    favoriteDestinations: [],
    interests: [],
    pace: 'balanced',
    travellingWithKids: false,
    accessibilityNeeds: '',
    dietaryNeeds: [],
};

export const DEFAULT_NOTIFICATIONS: NotificationSettings = {
    priceDropAlerts: true,
    bookingReminders: true,
    weeklyInspiration: false,
    productUpdates: false,
};

export const EMPTY_PROFILE: ProfileState = {
    userId: '',
    displayName: '',
    bio: '',
    homeCity: '',
    preferences: DEFAULT_PREFERENCES,
    notifications: DEFAULT_NOTIFICATIONS,
    bookings: [],
    savedTrips: [],
    activity: [],
};
